import { createPortal } from 'react-dom';
import { useSlotCanvasStore } from '@/store/slotCanvasStore';
import { useRegisterSlotCanvas } from '../../slot-canvas/useRegisterSlotCanvas';
import { SlotCanvasLayout } from '../../slot-canvas/SlotCanvasLayout';
import { useChatMetadata } from '@/contexts/ChatMetadataContext';
import { TaskDetailBody } from '@/components/tasks/detail/TaskDetailBody';
import { TaskDetailFooter } from '@/components/tasks/detail/TaskDetailFooter';
import { useOpenTaskInChat } from '@/components/tasks/useOpenTaskInChat';
import { ArtifactCanvasSlot, artifactSlotId } from '@/components/artifact-canvas';
import { useTaskArtifacts } from '@/hooks/useArtifacts';
import { TASK_PANEL_WIDTH_PX } from '@/components/tasks/taskTheme';

/** The canvas is one slot per task, so two cards for the same task share one. */
export const taskSlotId = (taskId: string) => `task_detail-${taskId}`;


export interface TaskDetailSlotProps {
  taskId: string;
  /** The card's title, which names the slot before the task itself is fetched. */
  title: string;
}

/**
 * Registers one task's details as a canvas slot, opened by clicking its card.
 *
 * Registration is not opening: cards are receipts that stay in the transcript,
 * so a conversation reloaded with five of them registers five slots and opens
 * none of them.
 *
 * The task's attachments get canvas slots of their own, mounted alongside so
 * one can be opened over the task and the back arrow returns here.
 */
export const TaskDetailSlot = ({ taskId, title }: TaskDetailSlotProps) => {
  const { workflowExecutionId } = useChatMetadata();
  const bodyMount = useSlotCanvasStore((state) => state.bodyMountElement);
  const openSlot = useSlotCanvasStore((state) => state.openSlot);
  const openInChat = useOpenTaskInChat();

  const { isOpen } = useRegisterSlotCanvas({
    id: taskSlotId(taskId),
    experience: 'task_detail',
    title,
    autoOpen: false,
    hasInStreamCard: true,
    preferredWidthPx: TASK_PANEL_WIDTH_PX,
  });

  /*
   * Read while the panel is open, so a transcript full of task cards fetches
   * for none of them up front, and opening the panel again re-reads: the URLs
   * an attachment carries are signed for the hour, and the chat may have
   * attached something since. What was read stays cached while an artifact of
   * its own is open, which is what keeps the slots below mounted.
   */
  const { data: attachments } = useTaskArtifacts(taskId, { enabled: isOpen });
  const attached = [ ...attachments?.notes ?? [], ...attachments?.outputs ?? [] ];
  const openedIds = new Set(attached.map((artifact) => artifact.id));

  /*
   * The artifact slots live outside the portal branch below. While one of them
   * is the open slot this task's `isOpen` is false, and a slot unmounted at
   * that moment would unregister the very slot the canvas is showing and close
   * it.
   */
  return (
    <>
      {[ ...openedIds ].map((artifactId) => (
        <ArtifactCanvasSlot
          key={artifactId}
          artifactId={artifactId}
          title={attached.find((artifact) => artifact.id === artifactId)?.title ?? 'Artifact'}
          autoOpen={false}
        />
      ))}

      {isOpen && bodyMount
        ? createPortal(
          <SlotCanvasLayout
            title={title}
            subtitle="Task"
            // The panel owns its own scroll and pins its own footer, so the
            // canvas body must not scroll it a second time or pad it a second
            // time.
            bodyClassName="flex flex-col overflow-hidden"
          >
            <TaskDetailBody
              taskId={taskId}
              currentExecutionId={workflowExecutionId}
              onOpenArtifact={(artifact) =>
                openSlot(artifactSlotId(artifact.id), { returnTo: taskSlotId(taskId) })}
              footer={(task, save) => (
                <TaskDetailFooter
                  task={task}
                  ctaLabel="Open in new chat"
                  onMarkDone={() => save({ status: 'COMPLETED' })}
                  onOpenInChat={() => openInChat(task, 'new_chat')}
                />
              )}
            />
          </SlotCanvasLayout>,
          bodyMount,
        )
        : null}
    </>
  );
};
